/** @file pickNode: the node under a screen point, by its joint head or else by its bone's axis line. */
import type { PoseBuffer } from '@zilo/pose3d-pose/contract';
import { POSE_LAYOUT as L } from '@zilo/pose3d-pose/layout/poseLayout';
import { screenOf } from '@zilo/pose3d-render-core/camera/screenOf';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import { boneTails } from './boneTails';
import { pickJoint } from './pickJoint';
import type { ScreenJoint } from './projectJoints';
import { segmentDistance } from './segmentDistance';

/** A picked node: its name and pose index. */
export type PickedNode = { name: string; index: number };

/** The other end of node `i`'s drawn bone: its parent joint (origin columns), or a root's stub tail. */
function boneFarEnd(pose: PoseBuffer, tails: Float64Array, i: number): [number, number, number] {
  const o = i * L.stride;
  const at = (c: number): number => pose[o + c] ?? 0;
  if (at(L.parentIndex) < 0) return [tails[3 * i] ?? 0, tails[3 * i + 1] ?? 0, tails[3 * i + 2] ?? 0];
  return [at(L.ox), at(L.oy), at(L.oz)];
}

/** The nearest bone axis (parent joint -> node, the drawn bone's centre line) of the shown joints within `radiusPx`. */
function nearestBone(joints: readonly ScreenJoint[], pose: PoseBuffer, n: number, view: View, at: readonly [number, number, number]): ScreenJoint | null {
  const tails = boneTails(pose, n, new Float64Array(3 * n), new Float64Array(n));
  let best: { joint: ScreenJoint; d: number } | null = null;
  for (const j of joints) {
    const d = segmentDistance(at[0], at[1], [j.x, j.y], screenOf(boneFarEnd(pose, tails, j.index), view));
    if (d <= at[2] && (best === null || d < best.d)) best = { joint: j, d };
  }
  return best?.joint ?? null;
}

/**
 * `pickJoint` on the joint heads first (radius `jointPx`, target pixels); else the bone whose axis line passes
 * nearest within `bonePx` selects its own node. `joints` are the shown joints (`jointsFor`), `pose` their pose.
 */
export function pickNode(joints: readonly ScreenJoint[], pose: PoseBuffer, view: View, p: readonly [number, number], jointPx = 8, bonePx = 4): PickedNode | null {
  const joint = pickJoint(joints, p[0], p[1], jointPx) ?? nearestBone(joints, pose, Math.floor(pose.length / L.stride), view, [p[0], p[1], bonePx]);
  return joint === null ? null : { name: joint.name, index: joint.index };
}
